"use client";

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Search, UserPlus, CheckCircle } from "lucide-react";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useAuth } from "@/hooks/use-auth";
import { postWithAuth } from "@/lib/api-helpers";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import type { WorkArea } from "@/types/workarea";
import type { Members, WorkspaceGroup } from "@/types/workspace";
import { MemberAvatar } from "@/components/tailwind/ui/memberAvatar";
import { giveGroupAccessToWorkArea } from "@/services-frontend/workspace/groupServices";

// Groups granted from this modal get "edit" — the closest equivalent to the
// "member" role that individuals get when added the same way. Per-group
// permission selection was removed intentionally for consistency with the
// member flow; if a finer-grained UI is needed later, surface it here.
const DEFAULT_GROUP_PERMISSION = "edit" as const;

interface WorkAreaAddMembersModalProps {
  workArea: WorkArea;
  isOpen: boolean;
  onClose: () => void;
  onMembersAdded?: () => void;
}

export default function WorkAreaAddMembersModal({
  workArea,
  isOpen,
  onClose,
  onMembersAdded,
}: WorkAreaAddMembersModalProps) {
  const { workspaceMembers, workspaceGroups, currentWorkspace, getMemberName } = useWorkspaceContext();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<Members[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Groups already granted access on this WorkArea — shown disabled in the
  // picker so users manage permission changes from the members tab instead.
  const grantedGroupIds = new Set(
    (workArea.groupAccess || []).map((g) => String(g.groupId)),
  );

  // Get members not already in the work area
  const workAreaMemberEmails = (workArea.members || []).map((m: any) => (m.userEmail || "").toLowerCase());
  const availableMembers = workspaceMembers.filter(
    (member) => !workAreaMemberEmails.includes(member.userEmail.toLowerCase())
  );

  // Filter available members by search query
  const filteredMembers = availableMembers.filter((member) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      member.userName?.toLowerCase().includes(query) ||
      member.userEmail?.toLowerCase().includes(query)
    );
  });

  // Close modal on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const toggleMemberSelection = (member: Members) => {
    setSelectedMembers((prev) => {
      const isSelected = prev.some((m) => m.userEmail === member.userEmail);
      if (isSelected) {
        return prev.filter((m) => m.userEmail !== member.userEmail);
      } else {
        return [...prev, member];
      }
    });
  };

  // Groups filtered by the search box (by group name).
  const filteredGroups = workspaceGroups.filter((group) => {
    if (!searchQuery) return true;
    return group.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const isGroupSelected = (groupId: string) => selectedGroups.includes(groupId);

  // Selecting a group adds it as a live access entry — dynamic membership
  // plus a fixed default permission. To add specific people from a group as
  // individuals instead, click them in the members list below.
  const toggleGroupSelection = (group: WorkspaceGroup) => {
    const gid = String(group.id);
    if (grantedGroupIds.has(gid)) {
      toast.error("This group already has access.");
      return;
    }
    setSelectedGroups((prev) =>
      prev.includes(gid) ? prev.filter((id) => id !== gid) : [...prev, gid],
    );
  };

  const handleSubmit = async () => {
    if (selectedMembers.length === 0 && selectedGroups.length === 0) {
      toast.error("Please select at least one member or group");
      return;
    }

    const workAreaId = workArea._id;
    if (!workAreaId) {
      toast.error("Missing work area ID");
      return;
    }

    setIsLoading(true);
    try {
      const tasks: Promise<unknown>[] = [];

      if (selectedMembers.length > 0) {
        tasks.push(
          postWithAuth("/api/workarea/add-people", {
            workAreaId,
            memberEmails: selectedMembers.map((m) => m.userEmail),
            role: "member",
          }).then((response) => {
            if (response && typeof response === "object" && "isError" in response && response.isError) {
              const err = response as { message?: string; error?: string };
              throw new Error(err.message || err.error || "Failed to add members");
            }
            if (response && typeof response === "object" && "error" in response && !("workArea" in response)) {
              const err = response as { error?: string; message?: string };
              throw new Error(err.error || err.message || "Failed to add members");
            }
          }),
        );
      }

      for (const groupId of selectedGroups) {
        tasks.push(
          giveGroupAccessToWorkArea({
            workAreaId,
            groupId,
            permission: DEFAULT_GROUP_PERMISSION,
          }),
        );
      }

      await Promise.all(tasks);

      const parts: string[] = [];
      if (selectedMembers.length > 0) {
        parts.push(`${selectedMembers.length} member${selectedMembers.length === 1 ? "" : "s"}`);
      }
      if (selectedGroups.length > 0) {
        parts.push(`${selectedGroups.length} group${selectedGroups.length === 1 ? "" : "s"}`);
      }
      toast.success(`Added ${parts.join(" and ")} to work area`);
      setSelectedMembers([]);
      setSelectedGroups([]);
      setSearchQuery("");
      onMembersAdded?.();
      onClose();
    } catch (error) {
      console.error("Error adding members/groups to work area:", error);
      toast.error(error instanceof Error ? error.message : "Failed to add");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || typeof window === "undefined") return null;

  return createPortal(
    <div
      // Bottom-anchored < lg (phones + iPads in portrait), centered at lg+.
      className="fixed inset-0 z-[11000] flex items-end lg:items-center justify-center"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/30 dark:bg-black/60" onClick={onClose} />
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        // < lg: full width, top-rounded, max 90dvh.
        // lg+ (laptops/desktops): identical to today — 500px wide, max-h 80vh.
        className="relative bg-white dark:bg-zinc-900 rounded-t-lg lg:rounded-lg shadow-lg border border-component w-full lg:w-[500px] lg:max-w-[90vw] max-h-[90dvh] lg:max-h-[80vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-component">
          <div>
            <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Add members to {workArea.name}
            </div>
            <div className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              Select workspace members or groups to add to this workarea
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover-component  rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-component">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500 dark:text-zinc-400" />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-component rounded-md bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Selected members + groups */}
        {(selectedMembers.length > 0 || selectedGroups.length > 0) && (
          <div className="p-4 border-b border-component">
            <div className="text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-2">
              Selected ({selectedMembers.length + selectedGroups.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedGroups.map((gid) => {
                const group = workspaceGroups.find((wg) => String(wg.id) === gid);
                return (
                  <div
                    key={`group-${gid}`}
                    className="flex items-center gap-2 px-2 py-1 bg-blue-100 dark:bg-blue-900/20 rounded-md text-sm"
                  >
                    <span className="text-zinc-900 dark:text-zinc-100">
                      {group?.name || "Group"}
                    </span>
                    <button
                      onClick={() => toggleGroupSelection(group ?? ({ id: gid, name: "", members: [], createdAt: "" } as WorkspaceGroup))}
                      title="Remove from selection"
                      className="hover:bg-blue-200 dark:hover:bg-blue-900/40 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              {selectedMembers.map((member) => (
                <div
                  key={member.userEmail}
                  className="flex items-center gap-2 px-2 py-1 bg-blue-100 dark:bg-blue-900/20 rounded-md text-sm"
                >
                  <span className="text-zinc-900 dark:text-zinc-100">
                    {getMemberName(member.userId) || getMemberName(member.userEmail) || member.userName || member.userEmail}
                  </span>
                  <button
                    onClick={() => toggleMemberSelection(member)}
                    title="Remove from selection"
                    className="hover:bg-blue-200 dark:hover:bg-blue-900/40 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Members list */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Groups — selecting one adds the whole group as a live access
              entry. Picking specific people from a group is still possible
              by clicking them in the members list below. */}
          {filteredGroups.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1 px-1">
                Groups
              </div>
              <div className="space-y-1">
                {filteredGroups.map((group) => {
                  const gid = String(group.id);
                  const alreadyGranted = grantedGroupIds.has(gid);
                  const selected = isGroupSelected(gid);
                  const count = group.members?.length ?? 0;
                  return (
                    <button
                      key={group.id}
                      onClick={() => toggleGroupSelection(group)}
                      title={alreadyGranted ? "This group already has access" : group.name}
                      disabled={alreadyGranted}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-left",
                        alreadyGranted
                          ? "opacity-60 cursor-not-allowed"
                          : "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                        selected && "bg-blue-50 dark:bg-blue-900/20",
                      )}
                    >
                      <MemberAvatar
                        name={group.name}
                        className="bg-zinc-200 dark:bg-zinc-700 rounded-full w-8 h-8 text-sm font-medium text-zinc-900 dark:text-zinc-100 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                          {group.name}
                        </div>
                        <div className="text-xs text-zinc-600 dark:text-zinc-400 truncate">
                          {alreadyGranted
                            ? "Already has access"
                            : `${count} member${count === 1 ? "" : "s"}`}
                        </div>
                      </div>
                      {selected && (
                        <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs font-medium text-zinc-600 dark:text-zinc-400 mt-3 mb-1 px-1">
                Members
              </div>
            </div>
          )}

          {filteredMembers.length === 0 ? (
            <div className="text-center py-8 text-sm text-zinc-500 dark:text-zinc-400">
              {searchQuery ? "No members found" : "No available members"}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredMembers.map((member) => {
                const isSelected = selectedMembers.some((m) => m.userEmail === member.userEmail);
                return (
                  <button
                    key={member.userEmail}
                    onClick={() => toggleMemberSelection(member)}
                    title={isSelected ? "Deselect member" : "Select member"}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-md hover-component  transition-colors text-left",
                      isSelected && "bg-blue-50 dark:bg-blue-900/20"
                    )}
                  >
                    <MemberAvatar
                      name={member.userName}
                      email={member.userEmail}
                      userId={member.userId}
                      className="bg-zinc-200 dark:bg-zinc-700 rounded-full w-8 h-8 text-sm font-medium text-zinc-900 dark:text-zinc-100 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                        {getMemberName(member.userId) || getMemberName(member.userEmail) || member.userName || member.userEmail}
                      </div>
                      <div className="text-xs text-zinc-600 dark:text-zinc-400 truncate">
                        {member.userEmail}
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-blue-500 dark:text-blue-500 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-component">
          <button
            onClick={onClose}
            title="Cancel"
            className="px-4 py-2 text-sm rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 hover-component "
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={(selectedMembers.length === 0 && selectedGroups.length === 0) || isLoading}
            title="Add selected"
            className={cn(
              "px-4 py-2 text-sm rounded-md bg-blue-500 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2",
              isLoading && "opacity-50 cursor-not-allowed"
            )}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Adding...</span>
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                <span>
                  Add
                  {selectedMembers.length + selectedGroups.length > 0
                    ? ` ${selectedMembers.length + selectedGroups.length}`
                    : ""}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

